import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useVehicles } from "../../context/VehicleContext";
import { useTranslation } from "../../hooks/useTranslation";
import VideoBackground from "./VideoBackground";
import { MEDIA_URL } from '../../config/constants';

const Hero = () => {
  const navigate = useNavigate();
  const { selectedDates, setSelectedDates } = useVehicles();
  const { t, getLocalizedPath } = useTranslation('home');
  const [pickupDate, setPickupDate] = useState(selectedDates.pickupDate || "");
  const [dropoffDate, setDropoffDate] = useState(selectedDates.dropoffDate || "");
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectionStep, setSelectionStep] = useState("pickup"); // "pickup" or "dropoff"
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDateSelect = (date) => {
    // Create date string without timezone offset
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateString = `${year}-${month}-${day}`;

    if (selectionStep === "pickup") {
      setPickupDate(dateString);
      setSelectedDates(prev => ({ ...prev, pickupDate: dateString }));
      
      // If dropoff is before pickup, clear it and move to dropoff selection
      if (dropoffDate && new Date(dropoffDate) <= new Date(dateString)) {
        setDropoffDate("");
        setSelectedDates(prev => ({ ...prev, dropoffDate: "" }));
      }
      
      // Move to dropoff selection step
      setSelectionStep("dropoff");
    } else {
      // Only allow dropoff dates after pickup date
      if (pickupDate && new Date(dateString) <= new Date(pickupDate)) {
        alert(t('hero.alertDropoffAfterPickup'));
        return;
      }
      setDropoffDate(dateString);
      setSelectedDates(prev => ({ ...prev, dropoffDate: dateString }));
      
      // Close calendar after selecting dropoff
      setShowCalendar(false);
      setSelectionStep("pickup"); // Reset for next time
    }
  };

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const generateCalendarDays = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate comparison

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    // Get the first day of the week (Sunday = 0)
    const startDay = firstDayOfMonth.getDay();

    // Parse dates for range calculation
    const pickupDateObj = pickupDate ? new Date(pickupDate) : null;
    const dropoffDateObj = dropoffDate ? new Date(dropoffDate) : null;
    
    if (pickupDateObj) pickupDateObj.setHours(0, 0, 0, 0);
    if (dropoffDateObj) dropoffDateObj.setHours(0, 0, 0, 0);

    const days = [];

    // Add previous month's trailing days
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(prevYear, prevMonth, prevMonthLastDay - i);
      date.setHours(0, 0, 0, 0);

      const isPast = date < today;
      const currentDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const isPickupSelected = currentDateString === pickupDate;
      const isDropoffSelected = currentDateString === dropoffDate;
      const isSelected = isPickupSelected || isDropoffSelected;
      
      // Check if date is in range
      const isInRange = pickupDateObj && dropoffDateObj && 
        date > pickupDateObj && date < dropoffDateObj;
      
      // Check if date is unavailable for dropoff (before or same as pickup)
      const isUnavailable = selectionStep === "dropoff" && pickupDateObj && date <= pickupDateObj;

      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isPast,
        isSelected,
        isInRange,
        isUnavailable,
      });
    }

    // Add current month's days
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(currentYear, currentMonth, day);
      date.setHours(0, 0, 0, 0);

      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;
      const currentDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const isPickupSelected = currentDateString === pickupDate;
      const isDropoffSelected = currentDateString === dropoffDate;
      const isSelected = isPickupSelected || isDropoffSelected;
      
      // Check if date is in range
      const isInRange = pickupDateObj && dropoffDateObj && 
        date > pickupDateObj && date < dropoffDateObj;
      
      // Check if date is unavailable for dropoff (before or same as pickup)
      const isUnavailable = selectionStep === "dropoff" && pickupDateObj && date <= pickupDateObj;

      days.push({
        date,
        day: day,
        isCurrentMonth: true,
        isToday,
        isPast,
        isSelected,
        isInRange,
        isUnavailable,
      });
    }

    // Add next month's leading days to fill the grid
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    let nextMonthDay = 1;
    while (days.length < 42) {
      const date = new Date(nextYear, nextMonth, nextMonthDay);
      date.setHours(0, 0, 0, 0);

      const isPast = date < today;
      const currentDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const isPickupSelected = currentDateString === pickupDate;
      const isDropoffSelected = currentDateString === dropoffDate;
      const isSelected = isPickupSelected || isDropoffSelected;
      
      // Check if date is in range
      const isInRange = pickupDateObj && dropoffDateObj && 
        date > pickupDateObj && date < dropoffDateObj;
      
      // Check if date is unavailable for dropoff (before or same as pickup)
      const isUnavailable = selectionStep === "dropoff" && pickupDateObj && date <= pickupDateObj;

      days.push({
        date,
        day: nextMonthDay,
        isCurrentMonth: false,
        isToday: false,
        isPast,
        isSelected,
        isInRange,
        isUnavailable,
      });

      nextMonthDay++;
    }

    return days;
  };

  const handleDateClick = (type) => {
    setShowCalendar(!showCalendar);
    setSelectionStep(type);
  };

  const handleBooking = () => {
    if (pickupDate && dropoffDate) {
      // Update context with selected dates
      setSelectedDates({ pickupDate, dropoffDate });
      // Navigate to catalog page
      navigate(getLocalizedPath('/catalog'));
    } else {
      alert(t('hero.alertSelectDates'));
    }
  };

  return (
    <div className="hero-section">
      <VideoBackground />
      <div className="hero-content">
        <div className="hero-header-container">
          <div className="hero-text-content">
            <h1 className="main-heading">
              {t('hero.title')}
            </h1>
            <p className="subheading">
              {t('hero.subtitle')}
            </p>
          </div>
          <div className="hero-button-container">
            <div className="primary-button" onClick={() => navigate(getLocalizedPath('/catalog'))}>
              <div className="button-state-layer">{t('hero.exploreVans')}</div>
            </div>
          </div>
        </div>
        <div className="booking-form">
          <div className="pickup-date-section">
            <div className="date-input-container">
              <div className="date-input">
                <div
                  className="date-state-layer"
                  onClick={() => handleDateClick("pickup")}
                >
                  <div className="date-content">
                    <div className="input-text-container">
                      {pickupDate
                        ? formatDate(pickupDate)
                        : isMobile
                          ? t('hero.selectDateMobile')
                          : t('hero.selectPickupDate')}
                    </div>
                    <div className="label-text-container">{t('hero.pickupDate')}</div>
                  </div>
                  <div className="trailing-icon">
                    <div className="icon-container">
                      <div className="icon-state-layer">
                        <img
                          src={`${MEDIA_URL}/images/blog/blog-placeholder.webp`}
                          className="calendar-icon"
                          alt=""
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {showCalendar && (
                <div className="calendar-dropdown">
                  <div className="calendar-header">
                    <button
                      className="calendar-nav-btn"
                      onClick={() => navigateMonth("prev")}
                    >
                      &#8249;
                    </button>
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <h4>
                        {new Date(currentYear, currentMonth).toLocaleDateString(
                          "en-US",
                          { month: "long", year: "numeric" },
                        )}
                      </h4>
                      <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0 0" }}>
                        {selectionStep === "pickup"
                          ? t('hero.selectPickupCalendar')
                          : t('hero.selectDropoffCalendar')}
                      </p>
                    </div>
                    <button
                      className="calendar-nav-btn"
                      onClick={() => navigateMonth("next")}
                    >
                      &#8250;
                    </button>
                  </div>
                  <div className="calendar-grid">
                    <div className="calendar-weekdays">
                      {["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map(
                        (day) => (
                          <div key={day} className="weekday">
                            {t(`hero.weekdays.${day}`)}
                          </div>
                        ),
                      )}
                    </div>
                    <div className="calendar-days">
                      {generateCalendarDays().map((dayInfo, index) => (
                        <button
                          key={index}
                          className={`calendar-day ${dayInfo.isCurrentMonth ? "current-month" : ""} ${dayInfo.isToday ? "today" : ""} ${dayInfo.isPast ? "past" : ""} ${dayInfo.isSelected ? "selected" : ""} ${dayInfo.isInRange ? "in-range" : ""} ${dayInfo.isUnavailable ? "unavailable" : ""}`}
                          onClick={() =>
                            !dayInfo.isPast && !dayInfo.isUnavailable &&
                            handleDateSelect(dayInfo.date)
                          }
                          disabled={dayInfo.isPast || dayInfo.isUnavailable}
                        >
                          {dayInfo.day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="date-input-container">
              <div className="date-input">
                <div
                  className="date-state-layer"
                  onClick={() => handleDateClick("dropoff")}
                >
                  <div className="date-content">
                    <div className="input-text-container">
                      {dropoffDate
                        ? formatDate(dropoffDate)
                        : isMobile
                          ? t('hero.selectDateMobile')
                          : t('hero.selectDropoffDate')}
                    </div>
                    <div className="label-text-container">{t('hero.dropoffDate')}</div>
                  </div>
                  <div className="trailing-icon">
                    <div className="icon-container">
                      <div className="icon-state-layer">
                        <img
                          src={`${MEDIA_URL}/images/blog/blog-placeholder.webp`}
                          className="calendar-icon"
                          alt=""
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button className="booking-button" onClick={handleBooking}>
            <div className="button-state-layer">{t('hero.bookNow')}</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Hero;
